import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Mail, CheckCircle2, AlertCircle } from 'lucide-react';

const forgotSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

export const ForgotPasswordPage: React.FC = () => {
  const { resetPassword } = useAuth();
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
  });

  const onSubmit = async (data: ForgotFormData) => {
    try {
      setIsSubmitting(true);
      setApiError(null);
      const { error } = await resetPassword(data.email);
      if (error) {
        setApiError(error.message);
      } else {
        setSuccess(true);
      }
    } catch (err: unknown) {
      setApiError(err instanceof Error ? err.message : 'Failed to send reset link');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-brand-border shadow-lg p-6 sm:p-8">
        <Link
          to="/login"
          className="inline-flex items-center text-xs text-brand-muted hover:text-brand-black mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Login
        </Link>

        <h1 className="text-xl font-bold text-brand-black">Reset Password</h1>
        <p className="text-xs text-brand-muted mt-1 mb-6">
          Enter your staff email address and we will send you a password reset link.
        </p>

        {success ? (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md text-center text-xs text-brand-green">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-brand-green" />
            <p className="font-semibold">Reset Link Sent</p>
            <p className="mt-1 text-brand-muted">
              Check your inbox for instructions to reset your password.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {apiError && (
              <div className="p-3 bg-brand-red-light border border-brand-red/30 rounded-md text-brand-red text-xs flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{apiError}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
                Email Address
              </label>
              <input
                type="email"
                placeholder="name@alkuwaittech.com"
                {...register('email')}
                className="w-full px-3 py-2.5 bg-brand-bg border border-brand-border rounded-md text-sm text-brand-black placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-green"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-brand-red">{errors.email.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex items-center justify-center space-x-2 py-2.5 px-4 bg-brand-green text-white font-semibold text-sm rounded-md hover:bg-brand-green-hover transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  <span>Send Reset Link</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
